import React, { useEffect, useState } from 'react';
import { Button, Input } from '@cokitchen/cokitchen-components';
import { ReactComponent as VerifyAccountIcon } from 'assets/icons/verify-account.svg';
import { ROUTE_KEYS } from 'utils/constants';
import { useLocation, useNavigate } from 'react-router';
import useDashboardContext from 'contexts/dashboard.context';
import { apiService } from 'services/base.service';
import { handleApiError } from 'utils/functions';

const NewEmail = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const wasPreviousRouteChangeEmail =
    location.state && location.state.from === `/${ROUTE_KEYS.CHANGE_EMAIL}`;
  const wasOTPVerified = location.state && location.state.otpVerified;

  const [timeLeft, setTimeLeft] = useState(0);
  const [resendCount, setResendCount] = useState(0);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '' });
  const [formError, setFormError] = useState({ email: '' });

  const navigateToAccount = () => navigate(`/${ROUTE_KEYS.PROFILE}`);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (timeLeft > 0 && success) {
        setTimeLeft((prev) => prev - 1);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, success]);

  const formIsValid = () => {
    setFormError({ email: '' });
    if (!formData.email) {
      setFormError({ email: 'Email is required' });
      return false;
    }
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(formData.email)) {
      setFormError({ email: 'Invalid email address' });
      return false;
    }
    return true;
  };

  const { setHideSidebar, setBackLinkTitle, setIsBackLink, setBackLink } = useDashboardContext();
  useEffect(() => {
    setHideSidebar(true);
    setIsBackLink(true);
    setBackLinkTitle('Return to Account');
    setBackLink(ROUTE_KEYS.PROFILE);
    return () => {
      setHideSidebar(false);
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  const handleConfirm = async () => {
    if (!formIsValid()) return;
    const payload = {
      email: formData.email,
      dashboard_link: 'https://staging-business.getfoodcourt.com/auth/verify-email/'
    };
    setLoading(true);
    setTimeLeft(60 * 2 ** resendCount);
    setResendCount(resendCount + 1);
    try {
      const res = await apiService.put('member/change-email', { data: payload });
      if (res.status === 200) {
        setSuccess(true);
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  if (!wasPreviousRouteChangeEmail || !wasOTPVerified) {
    return (
      <div className='mx-auto max-w-[360px]'>
        <VerifyAccountIcon className='mb-8' />
        <p className='text-[32px] mb-4 font-semibold'>
          You haven&apos;t completed the email address verification process.
        </p>
        <p className='text-[#6F7376] max-mb-[80px]'>To change your email address:</p>
        <p className='text-sm text-[#3FB827] mt-2'>
          1. Go to your account settings and click on Change Email.
        </p>
        <p className='text-sm text-[#3FB827] mt-2'>
          2. Verify your current email address by entering the OTP.
        </p>
        <p className='text-sm text-[#3FB827] mt-2'>
          3. Once verified, you can proceed to update your email.
        </p>
        <Button
          small
          color='primary'
          className=' mt-[64px] w-[300px]'
          variant='block'
          onClick={navigateToAccount}>
          Continue to Profile
        </Button>
      </div>
    );
  }

  return !success ? (
    <div className='mx-auto max-w-[390px] '>
      <p className='text-[32px] font-semibold mb-4'>Provide new email</p>
      <p className='text-[#6F7376] text-[16px] mb-12'>Ensure to provide a correct email address</p>
      <div>
        <Input
          design='bordered'
          placeholder='Email'
          label='Email'
          type='email'
          value={formData.email}
          error={formError.email}
          onChange={(e) => {
            setFormError({ email: '' });
            setFormData({ email: e.target.value });
          }}
        />
        <Button
          small
          color='primary'
          className='mx-auto mt-[64px]'
          variant='block'
          onClick={handleConfirm}
          loading={loading}
          disabled={!formData.email}>
          Continue
        </Button>
      </div>
    </div>
  ) : (
    <div className='mx-auto max-w-[360px]'>
      <VerifyAccountIcon className='mb-8' />

      <p className='text-[32px] mb-4 font-semibold'>Confirm new email</p>
      <p className='text-[#6F7376] max-mb-[80px]'>
        A verification link has been sent to{' '}
        <span className='text-[#0080E5] text-base font-medium mx-1'>{formData.email}.</span> Pls
        check your email and click the link to confirm
      </p>
      {timeLeft === 0 ? null : (
        <div className='text-sm'>
          <p className='text-[#3FB827] mt-6'>
            Didn’t receive code?
            <span className='text-[#2E3031] pl-1 text-medium'>Resend in {timeLeft} seconds</span>
          </p>
        </div>
      )}
      <Button
        small
        color='primary'
        className=' mt-[64px] w-[300px]'
        variant='block'
        onClick={handleConfirm}
        loading={loading}
        disabled={timeLeft > 0}>
        Resend
      </Button>
    </div>
  );
};

export default NewEmail;
